/**
 * レスポンシブデザイン用カスタムフック
 * 画面サイズに応じた条件分岐とユーティリティを提供
 */
import { useState, useEffect } from 'react';

// ブレイクポイント定義（Tailwind CSS準拠）
const BREAKPOINTS = {
  xs: 375,
  sm: 640,
  md: 768,
  lg: 1024,
  xl: 1280,
  '2xl': 1536,
};

/**
 * 現在の画面サイズとデバイスタイプを取得するフック
 */
export function useResponsive() {
  // SSR対応：初期状態を更安全に設定
  const [windowSize, setWindowSize] = useState({
    width: typeof window !== 'undefined' ? window.innerWidth : 1024, // デスクトップをデフォルト
    height: typeof window !== 'undefined' ? window.innerHeight : 768,
  });

  // ハイドレーション完了フラグ
  const [isHydrated, setIsHydrated] = useState(false);

  useEffect(() => {
    // クライアントサイドでの初期化
    if (typeof window !== 'undefined') {
      setIsHydrated(true);
      setWindowSize({
        width: window.innerWidth,
        height: window.innerHeight,
      });
    }

    const handleResize = () => {
      if (typeof window !== 'undefined') {
        setWindowSize({
          width: window.innerWidth,
          height: window.innerHeight,
        });
      }
    };

    // デバウンス処理
    let timeoutId;
    const debouncedResize = () => {
      clearTimeout(timeoutId);
      timeoutId = setTimeout(handleResize, 150);
    };

    if (
      typeof window !== 'undefined' &&
      window.addEventListener &&
      typeof window.addEventListener === 'function'
    ) {
      window.addEventListener('resize', debouncedResize);
      handleResize(); // 初期値設定
    }

    return () => {
      if (
        typeof window !== 'undefined' &&
        window.removeEventListener &&
        typeof window.removeEventListener === 'function'
      ) {
        window.removeEventListener('resize', debouncedResize);
      }
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
    };
  }, []);

  // デバイスタイプ判定
  const isMobile = windowSize.width < BREAKPOINTS.md;
  const isTablet =
    windowSize.width >= BREAKPOINTS.md && windowSize.width < BREAKPOINTS.lg;
  const isDesktop = windowSize.width >= BREAKPOINTS.lg;

  // 詳細なブレイクポイント判定
  const isXs = windowSize.width < BREAKPOINTS.sm;
  const isSm =
    windowSize.width >= BREAKPOINTS.sm && windowSize.width < BREAKPOINTS.md;
  const isMd =
    windowSize.width >= BREAKPOINTS.md && windowSize.width < BREAKPOINTS.lg;
  const isLg =
    windowSize.width >= BREAKPOINTS.lg && windowSize.width < BREAKPOINTS.xl;
  const isXl =
    windowSize.width >= BREAKPOINTS.xl && windowSize.width < BREAKPOINTS['2xl'];
  const is2xl = windowSize.width >= BREAKPOINTS['2xl'];

  // 縦向き/横向き判定
  const isPortrait = windowSize.height > windowSize.width;
  const isLandscape = windowSize.width > windowSize.height;

  // タッチデバイス判定
  const isTouchDevice =
    typeof window !== 'undefined' &&
    ('ontouchstart' in window || navigator.maxTouchPoints > 0);

  // ユーティリティ関数
  const getResponsiveValue = (mobileValue, tabletValue, desktopValue) => {
    if (isMobile) return mobileValue;
    if (isTablet) return tabletValue ?? desktopValue;
    return desktopValue;
  };

  // レスポンシブクラス生成
  const getResponsiveClasses = (baseClasses, responsiveClasses = {}) => {
    let classes = baseClasses || '';

    if (responsiveClasses.mobile && isMobile) {
      classes += ` ${responsiveClasses.mobile}`;
    }
    if (responsiveClasses.tablet && isTablet) {
      classes += ` ${responsiveClasses.tablet}`;
    }
    if (responsiveClasses.desktop && isDesktop) {
      classes += ` ${responsiveClasses.desktop}`;
    }

    return classes.trim();
  };

  return {
    // 画面サイズ
    windowSize,
    width: windowSize.width,
    height: windowSize.height,

    // ハイドレーション状態
    isHydrated,

    // デバイスタイプ
    isMobile,
    isTablet,
    isDesktop,

    // 詳細ブレイクポイント
    isXs,
    isSm,
    isMd,
    isLg,
    isXl,
    is2xl,

    // 向き
    isPortrait,
    isLandscape,

    // タッチデバイス
    isTouchDevice,

    // ユーティリティ
    getResponsiveValue,
    getResponsiveClasses,

    // ブレイクポイント定数
    breakpoints: BREAKPOINTS,
  };
}

/**
 * メディアクエリを監視するフック
 */
export function useMediaQuery(query) {
  const [matches, setMatches] = useState(false);

  useEffect(() => {
    if (
      typeof window === 'undefined' ||
      !window.matchMedia ||
      typeof window.matchMedia !== 'function'
    ) {
      return;
    }

    const media = window.matchMedia(query);
    if (!media) return;

    const listener = () => setMatches(media.matches);

    // 初期値設定
    setMatches(media.matches);

    // リスナー登録
    if (
      media.addEventListener &&
      typeof media.addEventListener === 'function'
    ) {
      media.addEventListener('change', listener);
    } else if (media.addListener && typeof media.addListener === 'function') {
      media.addListener(listener); // 旧ブラウザ対応
    }

    return () => {
      if (
        media.removeEventListener &&
        typeof media.removeEventListener === 'function'
      ) {
        media.removeEventListener('change', listener);
      } else if (
        media.removeListener &&
        typeof media.removeListener === 'function'
      ) {
        media.removeListener(listener);
      }
    };
  }, [query]);

  return matches;
}

/**
 * プリセットメディアクエリ
 */
export function useBreakpoint(breakpoint) {
  const query = `(min-width: ${BREAKPOINTS[breakpoint]}px)`;
  return useMediaQuery(query);
}
